import React, { useEffect, useState } from 'react'
import { CssBaseline, ThemeProvider, Box, CircularProgress } from '@mui/material'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { QueryClientProvider, QueryClient, MutationCache } from '@tanstack/react-query'
import axios from 'axios'
import { cacheExchange, fetchExchange, Provider, createClient, mapExchange } from 'urql'
import theme from './theme'
import CampaignList from './CampaignList'
import CreateCampaign from './CreateCampaign'
import CampaignDetails from './CampaignDetails'
import CreateFlight from './CreateFlight'
import FlightTargets from './FlightTargets'
import LoginScreen from './login/LoginScreen'
import AppHeader from './AppHeader'
import { UserAuthResponse } from '../types/campaign'
import AlertMessage, { AlertContents } from './AlertMessage'

const axiosDefaults = {}
export const http = axios.create(axiosDefaults)
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: () => {
      queryClient.invalidateQueries()
    }
  })
})

function App() {
  const [showLogin, setShowLogin] = useState<boolean>(false)
  const [firstLoad, setFirstLoad] = useState<boolean>(true)
  const [hasPermissions, setHasPermissions] = useState<boolean>(true)
  const [userAuthResponse, setUserAuthResponse] = useState<UserAuthResponse | null>(null)
  const [globalAlert, setGlobalAlert] = useState<AlertContents | null>(null)

  const checkAuth = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_REST_PLATFORM_API_PATH}/v2/auth/me`,
        {
          withCredentials: true
        }
      )

      if (response.status === 200) {
        const { company } = response.data

        localStorage.setItem('companyName', company?.name)
        setUserAuthResponse(response.data)
        return true
      } else {
        return false
      }
    } catch (_error) {
      return false
    }
  }

  useEffect(() => {
    const checkIfAuthenticated = async () => {
      const auth = await checkAuth()
      setFirstLoad(false)
      setShowLogin(!auth)
    }
    checkIfAuthenticated()
  }, [])

  http.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status
      const requestUrl = error?.config?.url || ''

      // Only log out for 401s from auth endpoints, not resource endpoints
      const isAuthEndpoint = requestUrl.includes('/auth/')

      if (status === 401 && isAuthEndpoint) {
        localStorage.clear()
        setUserAuthResponse(null)
        setShowLogin(true)
        setHasPermissions(false)
      } else if (status === 403) {
        setGlobalAlert({
          open: true,
          severity: 'error',
          message:
            error?.response?.data?.message || 'You do not have permission to perform this action.'
        })
      }
      return Promise.reject(error)
    }
  )

  const networkClient = createClient({
    url: process.env.REACT_APP_GRAPH_QL_PATH as string,
    exchanges: [
      cacheExchange,
      mapExchange({
        onError: (error) => {
          const isForbidden = error.graphQLErrors.some((e) => e.extensions?.code === 'FORBIDDEN')
          if (isForbidden) {
            setGlobalAlert({
              open: true,
              severity: 'error',
              message: 'You do not have permission to perform this action.'
            })
            return
          }
        }
      }),
      fetchExchange
    ],
    fetchOptions: {
      credentials: 'include'
    }
  })

  if (firstLoad) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    )
  }

  const Wrapper = ({
    children,
    backgroundColor
  }: {
    children: React.ReactNode
    backgroundColor?: string
  }) => {
    return (
      <Box>
        <AppHeader user={userAuthResponse} backgroundColor={backgroundColor} />
        {globalAlert?.open && (
          <AlertMessage
            alert={globalAlert}
            onClose={() => setGlobalAlert(null)}
            backgroundColor="transparent"
          />
        )}
        {children}
      </Box>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Sentry.ErrorBoundary fallback={<div>An error has occurred</div>}>
        <CssBaseline />
        <Provider value={networkClient}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <Routes>
                {showLogin && (
                  <Route path="/*" element={<LoginScreen hasPermissions={hasPermissions} />} />
                )}
                {!showLogin && (
                  <>
                    <Route
                      path="/*"
                      element={
                        <Wrapper>
                          <CampaignList />
                        </Wrapper>
                      }
                    />
                    <Route
                      path="/campaign/create"
                      element={
                        <Wrapper>
                          <CreateCampaign user={userAuthResponse} />
                        </Wrapper>
                      }
                    />
                    <Route
                      path="/campaign/:id"
                      element={
                        <Wrapper backgroundColor={theme.palette['base-white']}>
                          <CampaignDetails user={userAuthResponse} />
                        </Wrapper>
                      }
                    />
                    <Route
                      path="/campaign/:id/edit"
                      element={
                        <Wrapper>
                          <CreateCampaign user={userAuthResponse} />
                        </Wrapper>
                      }
                    />
                    <Route
                      path="/campaign/:id/flight/create"
                      element={
                        <Wrapper>
                          <CreateFlight />
                        </Wrapper>
                      }
                    />
                    <Route
                      path="/campaign/:id/flight/:flightId/targets"
                      element={
                        <Wrapper>
                          <FlightTargets user={userAuthResponse} />
                        </Wrapper>
                      }
                    />
                    <Route
                      path="/campaign/:id/flight/:flightId" // Flight Edit
                      element={
                        <Wrapper>
                          <CreateFlight />
                        </Wrapper>
                      }
                    />
                  </>
                )}
              </Routes>
            </BrowserRouter>
          </QueryClientProvider>
        </Provider>
      </Sentry.ErrorBoundary>
    </ThemeProvider>
  )
}

export default App
